// Step12.js - Staff Feedback/Staffing Issues (Fixed dropdown values)
import React from 'react';
import ModernDataTable from '../ui/ModernDataTable';

const Step12 = ({ staffFeedbackItems, setStaffFeedbackItems, defaultDateTime }) => {
  const feedbackCategories = [
    { value: 'Performance commendation', label: 'Performance commendation' },
    { value: 'Performance failure', label: 'Performance failure' },
    { value: 'Other staffing issue', label: 'Other staffing issue' }
  ];

  const staffFeedbackFields = [
    {
      name: 'feedbackTime',
      type: 'datetime',
      label: 'Time feedback was observed/received',
      header: 'Feedback time',
      required: true,
      maxDate: new Date().toISOString(),
      validationMessage: 'Please enter when the staff performance was observed or feedback was received (cannot be in the future)',
      hint: 'Record the exact time when you observed the staff performance or when feedback about staff was received.'
    },
    {
      name: 'feedbackCategory',
      type: 'dropdown',
      label: 'Staff feedback category',
      header: 'Category',
      placeholder: 'Select the type of staff feedback',
      options: feedbackCategories,
      required: true,
      validationMessage: 'Please select the staff feedback category from the dropdown',
      hint: (
        <div>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>Choose the appropriate category:</p>
          <ul style={{ margin: 0, paddingLeft: '1rem' }}>
            <li><strong>Performance Commendation:</strong> Positive feedback about exceptional staff performance</li>
            <li><strong>Performance Failure:</strong> Negative feedback about poor or inappropriate staff performance</li>
            <li><strong>Other Staffing Issue:</strong> Administrative or operational staffing matters not related to individual performance</li>
          </ul>
        </div>
      )
    },
    {
      name: 'staffMemberName',
      type: 'text',
      label: 'Full name of staff member',
      header: 'Staff member',
      placeholder: 'First name and last name of the staff member',
      hideUntil: {
        condition: (data) => data.feedbackCategory === 'Performance commendation' || data.feedbackCategory === 'Performance failure'
      },
      conditionalRequired: {
        condition: (data) => data.feedbackCategory === 'Performance commendation' || data.feedbackCategory === 'Performance failure',
        message: 'Please enter the full name of the staff member'
      },
      hint: 'Enter the complete first and last name of the staff member involved in the performance feedback.'
    },
    {
      name: 'staffMemberDetails',
      type: 'text',
      label: 'Employee ID',
      header: 'Employee details',
      placeholder: 'Employee ID',
      hideUntil: {
        condition: (data) => data.feedbackCategory === 'Performance commendation' || data.feedbackCategory === 'Performance failure'
      },
      conditionalRequired: {
        condition: (data) => data.feedbackCategory === 'Performance commendation' || data.feedbackCategory === 'Performance failure',
        message: 'Please provide employee ID'
      },
      hint: 'Include the employee ID number'
    },
    {
      name: 'performanceType',
      type: 'dropdown',
      label: 'Type of performance observed',
      header: 'Performance type',
      placeholder: 'What type of performance was observed?',
      options: [
        { value: 'Customer Service Excellence', label: 'Customer Service Excellence' },
        { value: 'Teamwork and Collaboration', label: 'Teamwork and Collaboration' },
        { value: 'Initiative and Problem Solving', label: 'Initiative and Problem Solving' },
        { value: 'Safety and Compliance', label: 'Safety and Compliance' },
        { value: 'Communication Skills', label: 'Communication Skills' },
        { value: 'Professionalism and Attitude', label: 'Professionalism and Attitude' },
        { value: 'Leadership and Mentoring', label: 'Leadership and Mentoring' },
        { value: 'Attendance and Punctuality', label: 'Attendance and Punctuality' },
        { value: 'Policy or Procedure Violation', label: 'Policy or Procedure Violation' },
        { value: 'Inappropriate Behavior', label: 'Inappropriate Behavior' },
        { value: 'Poor Attitude or Service', label: 'Poor Attitude or Service' },
        { value: 'Other Performance Area', label: 'Other Performance Area' }
      ],
      hideUntil: {
        condition: (data) => data.feedbackCategory === 'Performance commendation' || data.feedbackCategory === 'Performance failure'
      },
      conditionalRequired: {
        condition: (data) => data.feedbackCategory === 'Performance commendation' || data.feedbackCategory === 'Performance failure',
        message: 'Please select the type of performance that was observed'
      },
      hint: 'Choose the category that best describes the type of performance or behavior you observed.'
    },
    {
      name: 'performanceDescription',
      type: 'textarea',
      label: 'Describe staff performance in detail',
      header: 'Performance description',
      placeholder: 'Provide specific details about the staff performance observed...',
      hideUntil: {
        condition: (data) => data.feedbackCategory === 'Performance commendation' || data.feedbackCategory === 'Performance failure'
      },
      conditionalRequired: {
        condition: (data) => data.feedbackCategory === 'Performance commendation' || data.feedbackCategory === 'Performance failure',
        message: 'Please provide a detailed description of the staff performance'
      },
      hint: (
        <div>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>For performance feedback, document:</p>
          <ul style={{ margin: 0, paddingLeft: '1rem' }}>
            <li>Specific actions or behaviors observed</li>
            <li>Context and circumstances of the situation</li>
            <li>Impact on patrons, colleagues, or operations</li>
            <li>Any witnesses present during the incident</li>
            <li>Exact quotes or conversations (if relevant)</li>
            <li>Duration and frequency of the behavior</li>
            <li>Comparison to expected standards or policies</li>
          </ul>
          <p style={{ margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>Be objective, factual, and specific in your description.</p>
        </div>
      )
    },
    {
      name: 'otherStaffingIssue',
      type: 'textarea',
      label: 'Other admin/staffing issue',
      header: 'Other issue',
      placeholder: 'Describe the other staffing or administrative issue...',
      hideUntil: {
        condition: (data) => data.feedbackCategory === 'Other staffing issue'
      },
      conditionalRequired: {
        condition: (data) => data.feedbackCategory === 'Other staffing issue',
        message: 'Please describe the other staffing or administrative issue'
      },
      hint: (
        <div>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>Other staffing issues might include:</p>
          <ul style={{ margin: 0, paddingLeft: '1rem' }}>
            <li>Scheduling conflicts or coverage issues</li>
            <li>Communication breakdowns</li>
            <li>Resource allocation or equipment problems</li>
            <li>Training needs or knowledge gaps</li>
            <li>Policy clarification requirements</li>
            <li>Administrative process improvements</li>
            </ul>
        </div>
      )
    },
  ];

  return (
    <ModernDataTable
      suppressTitle={true}
      data={staffFeedbackItems}
      setData={setStaffFeedbackItems}
      fields={staffFeedbackFields}
      addButtonText="Add Staff Feedback"
      emptyMessage="No staff feedback added yet."
      truncateLines={2}
      defaultDateTime={defaultDateTime}
    />
  );
};

export default Step12;